import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { APIService } from '../lib/apiService';
import { Employee } from '../lib/types';

// User interface for API authentication with role information
interface AuthUser {
  userId: number;
  telegramId: number;
  employee?: Employee;
}

interface APIAuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
}

const APIAuthContext = createContext<APIAuthContextType | null>(null);

export const useAPIAuth = () => {
  const context = useContext(APIAuthContext);
  if (!context) {
    throw new Error('useAPIAuth must be used within APIAuthProvider');
  }
  return context;
};

interface APIAuthProviderProps {
  children: ReactNode;
}

export const APIAuthProvider: React.FC<APIAuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check for existing authentication on mount
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        if (APIService.isAuthenticated()) {
          // Try to get current user info - this will validate the token
          try {
            const currentUser = await APIService.getCurrentUser();
            const profile = await APIService.getEmployeeProfile();
            
            setUser({
              userId: currentUser.userId,
              telegramId: currentUser.telegramId,
              employee: profile
            });
            setIsAuthenticated(true);
          } catch (userError: any) {
            // Check if the error indicates token expiration
            if (userError?.status === 401 || 
                (userError?.message && userError.message.toLowerCase().includes('token'))) {
              console.log('🚨 Token validation failed during auth check - clearing tokens');
              APIService.clearTokens();
            }
            setIsAuthenticated(false);
            setUser(null);
          }
        } else {
          setIsAuthenticated(false);
          setUser(null);
        }
      } catch (error) {
        console.error('🚨 Auth check failed:', error);
        
        // Set appropriate error message
        if (error instanceof Error) {
          if (error.message.includes('timeout') || error.message.includes('fetch')) {
            setError('Connection timeout. Please check your internet connection.');
          } else if (error.message.toLowerCase().includes('token') ||
                     error.message.toLowerCase().includes('unauthorized')) {
            setError('Your session has expired. Please login again.');
          } else {
            setError('Authentication check failed. Please login again.');
          }
        } else {
          setError('Authentication check failed');
        }
        
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthStatus();
    
    // Set up periodic token validation (every 2 minutes)
    const validationInterval = setInterval(() => {
      if (APIService.isAuthenticated()) {
        checkAuthStatus();
      }
    }, 2 * 60 * 1000);

    return () => clearInterval(validationInterval);
  }, []);

  const login = async (email: string, _password: string) => {
    try {
      setIsLoading(true);
      setError(null);

      // Note: For now, we'll simulate Telegram auth with email/password
      // In production, this would use actual Telegram initData
      const mockInitData = `user=${encodeURIComponent(JSON.stringify({
        id: Date.now(),
        first_name: email.split('@')[0],
        username: email.split('@')[0],
        language_code: 'en'
      }))}&auth_date=${Math.floor(Date.now() / 1000)}`;

      // Authenticate with backend API using mock Telegram data
      const authData = await APIService.authenticateWithTelegram(mockInitData);
      
      if (authData.accessToken) {
        console.log('✅ Login successful via API');
        
        // Get user profile
        const currentUser = await APIService.getCurrentUser();
        const profile = await APIService.getEmployeeProfile();
        
        setUser({
          userId: currentUser.userId,
          telegramId: currentUser.telegramId,
          employee: profile
        });
        setIsAuthenticated(true);
      } else {
        throw new Error('Login failed - no access token received');
      }
    } catch (error) {
      console.error('🚨 Login error:', error);
      
      let errorMessage = 'Login failed';
      if (error instanceof Error) {
        if (error.message.includes('Invalid Telegram')) {
          errorMessage = 'Authentication failed - invalid credentials';
        } else {
          errorMessage = error.message;
        }
      }
      
      setError(errorMessage);
      setIsAuthenticated(false);
      setUser(null);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      setIsLoading(true);
      await APIService.logout();
    } catch (error) {
      console.error('Logout error:', error);
      // Continue with logout even if API call fails
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      setIsLoading(false);
    }
  };

  const clearError = () => {
    setError(null);
  };

  const value: APIAuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    error,
    login,
    logout,
    clearError
  };

  return (
    <APIAuthContext.Provider value={value}>
      {children}
    </APIAuthContext.Provider>
  );
};